import type {
  CarouselBlock,
  CarouselItem,
  CvCardBlock,
  MessageBlock,
  MessageOption,
  OptionsBlock,
  TextBlock,
} from '../types';

// ---------------------------------------------------------------------------
// Dispatcher
// ---------------------------------------------------------------------------

interface BlocksRendererProps {
  blocks: MessageBlock[];
  /** クイックリプライタップ時に呼ばれる。`action_type === 'uri'` のオプションは
   *  ハンドラ側でなく BlocksRenderer 内で新規タブを開くため呼ばれない。 */
  onSelectOption: (option: MessageOption) => void;
  /** カルーセルカードのリンクを開く直前に呼ばれる (CV 計測用、任意)。 */
  onCarouselItemClick?: (item: CarouselItem) => void;
  /** CV カードのリンクを開く直前に呼ばれる (CV 計測用、任意)。 */
  onCvCardClick?: (block: CvCardBlock) => void;
  /** 同じ assistant メッセージ内の中で最新の OptionsBlock だけ enable する。
   *  false が渡されたらボタンは見た目だけ残してクリックを受け付けない。 */
  isLatestAssistantMessage: boolean;
  /** クイックリプライ / カルーセルのボタンを無効化する (送信中 / fatal 時) */
  disabled: boolean;
  t: (key: string) => string;
}

export function MessageBlocksRenderer(props: BlocksRendererProps) {
  return (
    <>
      {props.blocks.map((block, i) => (
        <Block
          key={i}
          block={block}
          onSelectOption={props.onSelectOption}
          onCarouselItemClick={props.onCarouselItemClick}
          onCvCardClick={props.onCvCardClick}
          isLatestAssistantMessage={props.isLatestAssistantMessage}
          disabled={props.disabled}
          t={props.t}
        />
      ))}
    </>
  );
}

interface BlockProps {
  block: MessageBlock;
  onSelectOption: (option: MessageOption) => void;
  onCarouselItemClick?: (item: CarouselItem) => void;
  onCvCardClick?: (block: CvCardBlock) => void;
  isLatestAssistantMessage: boolean;
  disabled: boolean;
  t: (key: string) => string;
}

function Block({
  block,
  onSelectOption,
  onCarouselItemClick,
  onCvCardClick,
  isLatestAssistantMessage,
  disabled,
  t,
}: BlockProps) {
  switch (block.type) {
    case 'text':
      return <TextBubble block={block} />;
    case 'options':
      return (
        <QuickReplies
          block={block}
          onSelectOption={onSelectOption}
          disabled={disabled || !isLatestAssistantMessage}
          t={t}
        />
      );
    case 'carousel':
      return <Carousel block={block} onCarouselItemClick={onCarouselItemClick} t={t} />;
    case 'cv_card':
      return <CvCard block={block} onCvCardClick={onCvCardClick} t={t} />;
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Text
// ---------------------------------------------------------------------------

function TextBubble({ block }: { block: TextBlock }) {
  return <div className="bubble assistant">{block.text}</div>;
}

// ---------------------------------------------------------------------------
// Quick reply
// ---------------------------------------------------------------------------

interface QuickRepliesProps {
  block: OptionsBlock;
  onSelectOption: (option: MessageOption) => void;
  disabled: boolean;
  t: (key: string) => string;
}

function QuickReplies({ block, onSelectOption, disabled, t }: QuickRepliesProps) {
  const handleClick = (opt: MessageOption) => {
    if (disabled) return;
    if (opt.action_type === 'uri') {
      window.open(opt.value, '_blank', 'noopener,noreferrer');
      return;
    }
    onSelectOption(opt);
  };

  return (
    <div className="qr-block">
      {block.text && <div className="bubble assistant">{block.text}</div>}
      <div className="qr-options" role="group" aria-label={t('a11y.quickReply')}>
        {block.options.map((opt) => (
          <button
            type="button"
            key={opt.id}
            className="qr-option"
            onClick={() => handleClick(opt)}
            disabled={disabled}
            aria-label={opt.label}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Carousel
// ---------------------------------------------------------------------------

function Carousel({
  block,
  onCarouselItemClick,
  t,
}: {
  block: CarouselBlock;
  onCarouselItemClick?: (item: CarouselItem) => void;
  t: (key: string) => string;
}) {
  return (
    <div className="carousel" role="list" aria-label={t('a11y.carousel')}>
      {block.items.map((item) => (
        <CarouselCard key={item.id} item={item} onCarouselItemClick={onCarouselItemClick} t={t} />
      ))}
    </div>
  );
}

function CarouselCard({
  item,
  onCarouselItemClick,
  t,
}: {
  item: CarouselItem;
  onCarouselItemClick?: (item: CarouselItem) => void;
  t: (key: string) => string;
}) {
  const hasLink = !!item.link_url;
  const buttonLabel = item.button_label || t('carousel.viewDetails');
  const openLink = () => {
    if (!hasLink) return;
    // CV 計測はベストエフォート (fire-and-forget)。遷移を待たせない
    onCarouselItemClick?.(item);
    window.open(item.link_url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="carousel-card" role="listitem">
      {/* image_url 空 = 商品固有の画像が無いカード（Dify 経路等は既定画像を持たない）。画像領域ごと省く */}
      {item.image_url && (
        <button
          type="button"
          className="carousel-image-button"
          onClick={openLink}
          disabled={!hasLink}
          aria-label={item.title}
        >
          <img
            className="carousel-image"
            src={item.image_url}
            alt={item.title}
            loading="lazy"
          />
        </button>
      )}
      <div className="carousel-body">
        {item.subtitle && <div className="carousel-subtitle">{item.subtitle}</div>}
        <div className="carousel-title">{item.title}</div>
        {item.description && <div className="carousel-description">{item.description}</div>}
        {item.price && <div className="carousel-price">{item.price}</div>}
      </div>
      <button
        type="button"
        className="carousel-button"
        onClick={openLink}
        disabled={!hasLink}
      >
        {buttonLabel}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CV card (owner-declared conversion target)
// ---------------------------------------------------------------------------

function CvCard({
  block,
  onCvCardClick,
  t,
}: {
  block: CvCardBlock;
  onCvCardClick?: (block: CvCardBlock) => void;
  t: (key: string) => string;
}) {
  const hasLink = !!block.link_url;
  const buttonLabel = block.button_label || t('cvCard.open');
  const openLink = () => {
    if (!hasLink) return;
    // CV 計測はベストエフォート (fire-and-forget)。遷移を待たせない
    onCvCardClick?.(block);
    window.open(block.link_url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="cv-card" role="group" aria-label={t('a11y.cvCard')}>
      <div className="cv-card-title">{block.title}</div>
      <button type="button" className="cv-card-button" onClick={openLink} disabled={!hasLink}>
        {buttonLabel}
      </button>
    </div>
  );
}
